import _ from "lodash";
import * as React from "react";
import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink } from "react-router-dom";
import { Grid, GridColumn, Header, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@/components/bp/bp-ui";
import { HomeModel, QueriesClient } from "@app/api2/api";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { getRunStatusAsString } from "@common/Utils";
import { UserContext } from "../Contexts/UserContext";
import BPUnauthorized from "../Common/BPUnauthorized";
import BPUnexpectedError from "../Common/BPUnexpectedError";
import { BPContainer } from "@/components/bp/bpcontainer";

const fetchHomeModel = async () => {
    const client = new QueriesClient();
    const result = await client.getUserRecentCasesAndRuns();
    return result?.model;
};

export const HomePage: React.FC = () => {
    const user = useContext(UserContext);

    const canAccessCases = user?.user?.canAccessCases;

    const { data: model, isLoading, error } = useQuery<HomeModel | undefined>({
        queryKey: ["homeModel"],
        queryFn: fetchHomeModel,
        enabled: !!canAccessCases
    });

    if (!canAccessCases) return <BPUnauthorized />;
    if (isLoading) return null;
    if (error) return <BPUnexpectedError />;

    return (
        <BPContainer>
            <Grid>
                <GridColumn width={8}>
                    <Header attached={'top'}>{window.translation.home.recentCases}</Header>
                    <Table striped attached>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>{window.translation.common.lab}</TableHeaderCell>
                                <TableHeaderCell>{window.translation.common.case}</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {model?.recentCases.map(c => (
                                <TableRow key={c.caseId}>
                                    <TableCell>{c.labName}</TableCell>
                                    <TableCell>
                                        <NavLink to={buildUrl(AppRoutes.CaseDetails, { caseId: c.caseId, labId: c.labId })}>
                                            {c.caseName}
                                        </NavLink>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </GridColumn>
                <GridColumn width={8}>
                    <Header attached={'top'}>{window.translation.home.recentRuns}</Header>
                    <Table striped attached>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>{window.translation.common.lab}</TableHeaderCell>
                                <TableHeaderCell>{window.translation.common.case}</TableHeaderCell>
                                <TableHeaderCell>{window.translation.common.run}</TableHeaderCell>
                                <TableHeaderCell>{window.translation.common.status}</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {model?.recentRuns.map(c => (
                                <TableRow key={c.caseRunId}>
                                    <TableCell>{c.labName}</TableCell>
                                    <TableCell>
                                        <NavLink to={buildUrl(AppRoutes.CaseDetails, { caseId: c.caseId, labId: c.labId })}>
                                            {c.caseName}
                                        </NavLink>
                                    </TableCell>
                                    <TableCell>
                                        <NavLink to={buildUrl(AppRoutes.CaseRunDetails, { caseId: c.caseId, labId: c.labId, caseRunId: c.caseRunId })}>
                                            {c.caseRunId}
                                        </NavLink>
                                    </TableCell>
                                    <TableCell>{getRunStatusAsString(c.runStatus)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </GridColumn>
            </Grid>
        </BPContainer>
    );
};